import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent as ReactDragEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import Button from "../../../shared/components/Button";
import { Box } from "../../../shared/components/Box";
import { Grid } from "../../../shared/components/Grid";
import { Layout } from "../../../layouts/Layout";
import {
  applyWorkflowNode,
  getWorkflowRuntime,
  loadWorkflowNodeCatalog,
  loadWorkflowFromDatabase,
  saveWorkflowToDatabase,
  startWorkflowRuntime,
  stopWorkflowRuntime,
} from "../api";
import { NodeConfig } from "../components/NodeConfig";
import { NodeCanvas } from "../components/NodeCanvas";
import { WorkflowSidebar } from "../components/WorkflowSidebar";
import { ContextMenu, Node } from "../components/Node";
import {
  createNode,
  definitionByKind,
  definitions,
  installNodeCatalog,
  type Edge,
  type GroupGraph,
  type NodeDefinition,
  type WorkflowNode,
} from "../model";
import {
  isConnectionCompatible,
  validateWorkflow,
  workflowEdgeKey,
} from "../validation";
import { edgeJumps, jumpPath, pathFromPoints, routeOrthogonal } from "../edge-routing";
import { GRID_SIZE, NODE_WIDTH, findOpenNodePosition, hasNodeCollision, nodeHeight } from "../node-geometry";
import "../pages/WorkflowPage.scss";

const CANVAS_WIDTH = 3200;
const CANVAS_HEIGHT = 2000;
const NODE_DRAG_TYPE = "application/x-owl-workflow-node";

type PortPosition = { x: number; y: number };
type PortPositions = Record<string, Record<string, PortPosition>>;
type CanvasRect = { left: number; right: number; top: number; bottom: number };
type WorkflowSnapshot = { nodes: WorkflowNode[]; edges: Edge[] };
export type WorkflowViewMode = "desktop" | "tablet" | "mobile";

function snapToGrid(value: number) {
  return Math.round(value / GRID_SIZE) * GRID_SIZE;
}

function isStarterWorkflow(nodes: WorkflowNode[], edges: Edge[]) {
  const starterNodeIDs = [
    "camera-01",
    "sampler-01",
    "motion-01",
    "alert-01",
    "feed-01",
  ];
  const starterEdges = ["camera-01->sampler-01", "sampler-01->motion-01"];
  const starterNode = (node: WorkflowNode) =>
    ["camera-01", "sampler-01", "motion-01", "alert-01", "feed-01"].includes(
      node.id,
    ) || node.id.startsWith("split-");
  return (
    nodes.length >= starterNodeIDs.length &&
    starterNodeIDs.every((id) => nodes.some((node) => node.id === id)) &&
    nodes.every(starterNode) &&
    starterEdges.every((edge) =>
      edges.some((item) => `${item.source}->${item.target}` === edge),
    ) &&
    edges.some((edge) => edge.target === "alert-01") &&
    edges.some((edge) => edge.target === "feed-01")
  );
}

function normalizeNodeTitle(node: WorkflowNode): WorkflowNode {
  const definition = definitionByKind[node.kind];
  if (!definition) return node;
  return {
    ...node,
    label: definition.label,
    title: definition.label,
  };
}

function hydrateWorkflowNode(node: WorkflowNode): WorkflowNode {
  const fallback = createNode(
    node.kind,
    node.id,
    Number.isFinite(node.x) ? node.x : 120,
    Number.isFinite(node.y) ? node.y : 120,
  );
  return {
    ...fallback,
    ...node,
    width: Number.isFinite(node.width) ? node.width : fallback.width,
    height: Number.isFinite(node.height) ? node.height : fallback.height,
    title: node.title || fallback.title,
    status: node.status || fallback.status,
    capability: fallback.capability,
    config: { ...fallback.config, ...node.config },
    properties: { ...fallback.properties, ...node.properties },
    inputPorts: node.inputPorts ?? fallback.inputPorts,
    outputPorts: fallback.outputPorts ?? node.outputPorts,
    group: node.group ?? fallback.group,
  };
}

export function WorkflowDesktopView({
  mode = "desktop",
}: {
  mode?: WorkflowViewMode;
}) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [running, setRunning] = useState(false);
  const [activityVersions, setActivityVersions] = useState<
    Record<string, number>
  >({});
  const [runtimeStarting, setRuntimeStarting] = useState(false);
  const [runtimeError, setRuntimeError] = useState("");
  const dragRef = useRef<{
    nodes: { id: string; offsetX: number; offsetY: number }[];
  } | null>(null);
  const connectionDragRef = useRef<{
    nodeID: string;
    side: "input" | "output";
    portID: string;
    x: number;
    y: number;
  } | null>(null);
  const connectionMovedRef = useRef(false);
  const suppressConnectionClickRef = useRef(false);
  const runtimeActivityRef = useRef<Record<string, number>>({});
  const marqueeRef = useRef<{
    startX: number;
    startY: number;
    currentX: number;
    currentY: number;
    shiftKey: boolean;
  } | null>(null);
  const panRef = useRef<{
    clientX: number;
    clientY: number;
    scrollLeft: number;
    scrollTop: number;
  } | null>(null);
  const [nodes, setNodes] = useState<WorkflowNode[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [nodeDefinitions, setNodeDefinitions] = useState<NodeDefinition[]>([]);
  const [selectedID, setSelectedID] = useState<string | null>(null);
  const [selectedIDs, setSelectedIDs] = useState<Set<string>>(() => new Set());
  const [marquee, setMarquee] = useState<{
    startX: number;
    startY: number;
    currentX: number;
    currentY: number;
  } | null>(null);
  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);
  const workflowLoadedRef = useRef(false);
  const historyRef = useRef<WorkflowSnapshot[]>([]);
  const [saved, setSaved] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [historyLength, setHistoryLength] = useState(0);
  const [inspectorOpen, setInspectorOpen] = useState(mode === "desktop");
  const [moveMode, setMoveMode] = useState(false);

  const recordHistory = useCallback(() => {
    historyRef.current = [
      ...historyRef.current,
      { nodes: nodesRef.current, edges: edgesRef.current },
    ].slice(-100);
    setHistoryLength(historyRef.current.length);
  }, []);

  const undo = () => {
    const previous = historyRef.current.pop();
    if (!previous) return;
    setHistoryLength(historyRef.current.length);
    setNodes(previous.nodes);
    setEdges(previous.edges);
    setSaved(false);
  };

  const reportFrame = useCallback((nodeID: string) => {
    const active = new Set([nodeID]);
    const pending = [nodeID];
    while (pending.length) {
      const source = pending.pop()!;
      edgesRef.current.forEach((edge) => {
        if (edge.source === source && !active.has(edge.target)) {
          active.add(edge.target);
          pending.push(edge.target);
        }
      });
    }
    setActivityVersions((current) => {
      const next = { ...current };
      active.forEach((id) => {
        next[id] = (next[id] ?? 0) + 1;
      });
      return next;
    });
  }, []);

  useEffect(() => {
    nodesRef.current = nodes;
    edgesRef.current = edges;
  }, [nodes, edges]);

  useEffect(() => {
    getWorkflowRuntime()
      .then((status) => {
        setRunning(status.state === "running");
        setRuntimeError(status.error ?? "");
      })
      .catch(() => setRuntimeError("Runtime unavailable"));
  }, []);

  useEffect(() => {
    if (!running) {
      runtimeActivityRef.current = {};
      return;
    }
    let active = true;
    const pollActivity = async () => {
      try {
        const status = await getWorkflowRuntime();
        if (!active) return;
        status.nodes.forEach((node) => {
          const previous = runtimeActivityRef.current[node.node_id] ?? 0;
          if (node.activity > previous) reportFrame(node.node_id);
          runtimeActivityRef.current[node.node_id] = node.activity;
        });
      } catch {
        // Runtime status errors are already surfaced by workflow controls.
      }
    };
    void pollActivity();
    const interval = window.setInterval(pollActivity, 250);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [reportFrame, running]);

  useEffect(() => {
    let active = true;
    Promise.all([loadWorkflowNodeCatalog(), loadWorkflowFromDatabase()])
      .then(([catalog, draft]) => {
        if (
          !active ||
          !Array.isArray(draft.nodes) ||
          !Array.isArray(draft.edges)
        )
          return;
        installNodeCatalog(catalog);
        setNodeDefinitions([...definitions]);
        const nextNodes = isStarterWorkflow(draft.nodes, draft.edges)
          ? []
          : draft.nodes.map(hydrateWorkflowNode).map(normalizeNodeTitle);
        setNodes(nextNodes);
        setEdges(nextNodes.length ? draft.edges : []);
        workflowLoadedRef.current = true;
      })
      .catch(() => {
        // Keep local draft or starter workflow when database is unavailable.
        if (active) workflowLoadedRef.current = true;
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!workflowLoadedRef.current) return;
    setSaved(false);
    const timeout = window.setTimeout(() => {
      saveWorkflowToDatabase({ nodes, edges })
        .then(() => {
          setSaved(true);
          setLastSavedAt(new Date());
        })
        .catch(() => setSaved(false));
    }, 400);
    return () => window.clearTimeout(timeout);
  }, [nodes, edges]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "z") {
        event.preventDefault();
        undo();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });
  const [connectingFrom, setConnectingFrom] = useState<{
    nodeID: string;
    portID: string;
  } | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    nodeID?: string;
    edge?: Edge;
    output?: { nodeID: string; portID: string };
    warning?: string;
    canvasPosition?: PortPosition;
    x: number;
    y: number;
  } | null>(null);
  const [copiedNode, setCopiedNode] = useState<WorkflowNode | null>(null);
  const [addNodePosition, setAddNodePosition] = useState<PortPosition | null>(
    null,
  );
  const [settingsNodeID, setSettingsNodeID] = useState<string | null>(null);
  const [connectionDrag, setConnectionDrag] = useState<{
    nodeID: string;
    side: "input" | "output";
    portID: string;
  } | null>(null);
  const [connectionCursor, setConnectionCursor] = useState<PortPosition | null>(
    null,
  );
  const [connectionInvalid, setConnectionInvalid] = useState(false);
  const [selectedEdge, setSelectedEdge] = useState<string | null>(null);
  const [portPositions, setPortPositions] = useState<PortPositions>({});
  const [nodeRects, setNodeRects] = useState<CanvasRect[]>([]);
  const [inspectorWidth, setInspectorWidth] = useState(300);
  const inspectorResizeRef = useRef(false);

  useEffect(() => {
    const move = (event: PointerEvent) => {
      if (!inspectorResizeRef.current) return;
      setInspectorWidth(
        Math.min(480, Math.max(240, window.innerWidth - event.clientX)),
      );
    };
    const up = () => {
      inspectorResizeRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, []);

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const measurePorts = () => {
      const bounds = canvas.getBoundingClientRect();
      const next: PortPositions = {};
      const nextRects: CanvasRect[] = [];
      canvas.querySelectorAll<HTMLElement>(".node").forEach((node) => {
        const rect = node.getBoundingClientRect();
        nextRects.push({
          left: rect.left - bounds.left + canvas.scrollLeft,
          right: rect.right - bounds.left + canvas.scrollLeft,
          top: rect.top - bounds.top + canvas.scrollTop,
          bottom: rect.bottom - bounds.top + canvas.scrollTop,
        });
      });
      canvas
        .querySelectorAll<HTMLElement>("[data-port-side]")
        .forEach((port) => {
          const nodeID = port.dataset.nodeId;
          const side = port.dataset.portSide as "input" | "output" | undefined;
          const portID = port.dataset.portId ?? side ?? "output";
          if (!nodeID || !side) return;
          const rect = port.getBoundingClientRect();
          next[nodeID] ??= {};
          next[nodeID][portID] = {
            x: rect.left - bounds.left + canvas.scrollLeft + rect.width / 2,
            y: rect.top - bounds.top + canvas.scrollTop + rect.height / 2,
          };
        });
      setPortPositions(next);
      setNodeRects(nextRects);
    };

    measurePorts();
    const observer = new ResizeObserver(measurePorts);
    observer.observe(canvas);
    canvas
      .querySelectorAll<HTMLElement>(".node")
      .forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [nodes]);

  useEffect(() => {
    const move = (event: PointerEvent) => {
      const pan = panRef.current;
      const canvas = canvasRef.current;
      if (pan && canvas) {
        event.preventDefault();
        canvas.scrollLeft = pan.scrollLeft - (event.clientX - pan.clientX);
        canvas.scrollTop = pan.scrollTop - (event.clientY - pan.clientY);
        return;
      }

      const connection = connectionDragRef.current;
      if (connection) {
        const distance = Math.hypot(
          event.clientX - connection.x,
          event.clientY - connection.y,
        );
        if (distance > 10) connectionMovedRef.current = true;
        if (canvas) {
          const bounds = canvas.getBoundingClientRect();
          setConnectionCursor({
            x: event.clientX - bounds.left + canvas.scrollLeft,
            y: event.clientY - bounds.top + canvas.scrollTop,
          });
          const targetHandle = document
            .elementFromPoint(event.clientX, event.clientY)
            ?.closest<HTMLElement>('[data-port-side="input"]');
          const targetNode = nodesRef.current.find(
            (node) => node.id === targetHandle?.dataset.nodeId,
          );
          const sourceNode = nodesRef.current.find(
            (node) => node.id === connection.nodeID,
          );
          setConnectionInvalid(
            Boolean(
              connection.side === "output" &&
              targetNode &&
              sourceNode &&
              !isConnectionCompatible(
                sourceNode,
                connection.portID,
                targetNode,
                targetHandle?.dataset.portId ?? "input",
              ),
            ),
          );
        }
        return;
      }

      const selection = marqueeRef.current;
      if (selection && canvas) {
        const bounds = canvas.getBoundingClientRect();
        selection.currentX = event.clientX - bounds.left + canvas.scrollLeft;
        selection.currentY = event.clientY - bounds.top + canvas.scrollTop;
        setMarquee({ ...selection });
        return;
      }

      const drag = dragRef.current;
      if (!drag || !canvas) return;

      const bounds = canvas.getBoundingClientRect();
      setNodes((current) => {
        const nextNodes = current.map((node) => {
          const draggedNode = drag.nodes.find((item) => item.id === node.id);
          return draggedNode
            ? {
                ...node,
                x: snapToGrid(
                  event.clientX -
                    bounds.left +
                    canvas.scrollLeft -
                    draggedNode.offsetX,
                ),
                y: snapToGrid(
                  event.clientY -
                    bounds.top +
                    canvas.scrollTop -
                    draggedNode.offsetY,
                ),
              }
            : node;
        });
        const movingIDs = new Set(drag.nodes.map((item) => item.id));
        return hasNodeCollision(nextNodes, movingIDs) ? current : nextNodes;
      });
      setSaved(false);
    };

    const up = (event: PointerEvent) => {
      const pan = panRef.current;
      if (pan) {
        panRef.current = null;
        const canvas = canvasRef.current;
        if (canvas?.hasPointerCapture(event.pointerId))
          canvas.releasePointerCapture(event.pointerId);
        if (canvas) canvas.style.cursor = "";
        return;
      }

      dragRef.current = null;

      const connection = connectionDragRef.current;
      const connectionMoved = connectionMovedRef.current;
      if (connection && connectionMoved) {
        const releasedHandle =
          event.target instanceof Element
            ? event.target.closest<HTMLElement>(".node-handle")
            : null;
        const targetNodeID = releasedHandle?.dataset.nodeId;
        const targetPortID = releasedHandle?.dataset.portId ?? "input";
        const releasedOnInput =
          releasedHandle?.dataset.portSide === "input" && targetNodeID;
        if (connection.side === "output" && releasedOnInput) {
          if (connection.nodeID !== targetNodeID) {
            recordHistory();
            setEdges((current) => {
              const duplicate = current.some(
                (edge) =>
                  edge.source === connection.nodeID &&
                  (edge.sourcePort ?? "output") === connection.portID &&
                  edge.target === targetNodeID &&
                  (edge.targetPort ?? "input") === targetPortID,
              );
              if (duplicate) return current;
              return [
                ...current.filter(
                  (edge) =>
                    !(
                      edge.source === connection.nodeID &&
                      (edge.sourcePort ?? "output") === connection.portID
                    ) &&
                    !(
                      edge.target === targetNodeID &&
                      (edge.targetPort ?? "input") === targetPortID
                    ),
                ),
                {
                  source: connection.nodeID,
                  sourcePort: connection.portID,
                  target: targetNodeID,
                  targetPort:
                    targetPortID === "input" ? undefined : targetPortID,
                },
              ];
            });
            setSaved(false);
          }
        } else if (!releasedHandle) {
          recordHistory();
          setEdges((current) =>
            current.filter((edge) =>
              connection.side === "output"
                ? edge.source !== connection.nodeID ||
                  (edge.sourcePort ?? "output") !== connection.portID
                : edge.target !== connection.nodeID ||
                  (edge.targetPort ?? "input") !== connection.portID,
            ),
          );
          setSaved(false);
        }
      }

      connectionDragRef.current = null;
      connectionMovedRef.current = false;
      suppressConnectionClickRef.current = connectionMoved;
      setConnectionDrag(null);
      setConnectionCursor(null);
      setConnectionInvalid(false);

      const selection = marqueeRef.current;
      if (selection) {
        const left = Math.min(selection.startX, selection.currentX);
        const right = Math.max(selection.startX, selection.currentX);
        const top = Math.min(selection.startY, selection.currentY);
        const bottom = Math.max(selection.startY, selection.currentY);
        const hitIDs = nodesRef.current
          .filter((node) => {
            const nodeRight = node.x + NODE_WIDTH;
            const nodeBottom = node.y + nodeHeight(node);
            return (
              node.x < right &&
              nodeRight > left &&
              node.y < bottom &&
              nodeBottom > top
            );
          })
          .map((node) => node.id);
        setSelectedIDs((current) => {
          const next = selection.shiftKey
            ? new Set(current)
            : new Set<string>();
          hitIDs.forEach((id) => next.add(id));
          return next;
        });
        if (hitIDs[0]) setSelectedID(hitIDs[0]);
        marqueeRef.current = null;
        setMarquee(null);
      }
    };

    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);

    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, [recordHistory]);

  const startDrag = (event: ReactPointerEvent, node: WorkflowNode) => {
    const bounds = canvasRef.current?.getBoundingClientRect();
    if (!bounds) return;
    recordHistory();

    const draggedNodes = selectedIDs.has(node.id)
      ? nodes.filter((item) => selectedIDs.has(item.id))
      : [node];

    dragRef.current = {
      nodes: draggedNodes.map((item) => ({
        id: item.id,
        offsetX:
          event.clientX -
          bounds.left +
          (canvasRef.current?.scrollLeft ?? 0) -
          item.x,
        offsetY:
          event.clientY -
          bounds.top +
          (canvasRef.current?.scrollTop ?? 0) -
          item.y,
      })),
    };
  };

  const startPan = (event: ReactPointerEvent<HTMLDivElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return false;
    event.preventDefault();
    panRef.current = {
      clientX: event.clientX,
      clientY: event.clientY,
      scrollLeft: canvas.scrollLeft,
      scrollTop: canvas.scrollTop,
    };
    canvas.setPointerCapture(event.pointerId);
    canvas.style.cursor = "grabbing";
    return true;
  };

  const startMarquee = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button === 1) {
      startPan(event);
      return;
    }

    if (event.target instanceof Element && event.target.closest(".node"))
      return;
    if (event.button === 0 && moveMode) {
      startPan(event);
      return;
    }
    const bounds = canvasRef.current?.getBoundingClientRect();
    const canvas = canvasRef.current;
    if (!bounds || !canvas) return;
    const startX = event.clientX - bounds.left + canvas.scrollLeft;
    const startY = event.clientY - bounds.top + canvas.scrollTop;
    marqueeRef.current = {
      startX,
      startY,
      currentX: startX,
      currentY: startY,
      shiftKey: event.shiftKey,
    };
    setMarquee({ startX, startY, currentX: startX, currentY: startY });
  };

  const selectNode = (nodeID: string, additive: boolean) => {
    setSelectedIDs((current) => {
      const next = additive ? new Set(current) : new Set<string>();
      if (additive && next.has(nodeID)) next.delete(nodeID);
      else next.add(nodeID);
      if (next.size === 0) next.add(nodeID);
      return next;
    });
    setSelectedID(nodeID);
    if (mode !== "desktop") setInspectorOpen(true);
  };

  const connect = (
    nodeID: string,
    side: "input" | "output",
    portID = "output",
  ) => {
    if (suppressConnectionClickRef.current) {
      suppressConnectionClickRef.current = false;
      return;
    }
    if (side === "output") {
      if (
        edges.some(
          (edge) =>
            edge.source === nodeID && (edge.sourcePort ?? "output") === portID,
        )
      )
        return;
      setConnectingFrom({ nodeID, portID });
      return;
    }

    if (
      connectingFrom &&
      connectingFrom.nodeID !== nodeID &&
      !edges.some(
        (edge) =>
          edge.source === connectingFrom.nodeID &&
          edge.target === nodeID &&
          (edge.targetPort ?? "input") === portID,
      )
    ) {
      recordHistory();
      setEdges((current) => [
        ...current,
        {
          source: connectingFrom.nodeID,
          sourcePort: connectingFrom.portID,
          target: nodeID,
          targetPort: portID === "input" ? undefined : portID,
        },
      ]);
      setSaved(false);
    }
    setConnectingFrom(null);
  };

  const startConnectionDrag = (
    event: ReactPointerEvent,
    nodeID: string,
    side: "input" | "output",
    portID?: string,
  ) => {
    const resolvedPortID = portID ?? side;
    const hasConnection = edges.some((edge) =>
      side === "output"
        ? edge.source === nodeID &&
          (edge.sourcePort ?? "output") === resolvedPortID
        : edge.target === nodeID &&
          (edge.targetPort ?? "input") === resolvedPortID,
    );
    if (side === "input" && !hasConnection) return;

    connectionDragRef.current = {
      nodeID,
      portID: resolvedPortID,
      side,
      x: event.clientX,
      y: event.clientY,
    };
    connectionMovedRef.current = false;
    setConnectionDrag({ nodeID, side, portID: resolvedPortID });
    setConnectionInvalid(false);
    setConnectionCursor({
      x:
        event.clientX -
        (canvasRef.current?.getBoundingClientRect().left ?? event.clientX) +
        (canvasRef.current?.scrollLeft ?? 0),
      y:
        event.clientY -
        (canvasRef.current?.getBoundingClientRect().top ?? event.clientY) +
        (canvasRef.current?.scrollTop ?? 0),
    });
  };

  const addNode = (definition: NodeDefinition, dropPosition?: PortPosition) => {
    const position = findOpenNodePosition(
      nodes,
      dropPosition?.x ?? addNodePosition?.x ?? 48 + (nodes.length % 4) * 240,
      dropPosition?.y ?? addNodePosition?.y ?? 336 + Math.floor(nodes.length / 4) * 168,
      definition.kind,
    );
    const next = createNode(
      definition.kind,
      `${definition.kind}-${nodes.length + 1}`,
      position.x,
      position.y,
    );

    recordHistory();
    setNodes((current) => [
      ...current,
      {
      ...next,
        title: definition.label,
      },
    ]);
    setSelectedIDs(new Set([next.id]));
    setSelectedID(next.id);
    setSaved(false);
    setAddNodePosition(null);
  };

  const allowNodeDrop = (event: ReactDragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.types.includes(NODE_DRAG_TYPE)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  };

  const dropNode = (event: ReactDragEvent<HTMLDivElement>) => {
    const kind = event.dataTransfer.getData(NODE_DRAG_TYPE);
    const definition = nodeDefinitions.find((item) => item.kind === kind);
    const canvas = canvasRef.current;
    if (!definition || !canvas) return;

    event.preventDefault();
    const bounds = canvas.getBoundingClientRect();
    addNode(definition, {
      x: event.clientX - bounds.left + canvas.scrollLeft,
      y: event.clientY - bounds.top + canvas.scrollTop,
    });
  };

  const addOutput = (nodeID: string) => {
    recordHistory();
    setNodes((current) =>
      current.map((node) =>
        node.id === nodeID
          ? {
              ...node,
              config: {
                ...node.config,
                outputs: String(
                  Math.max(2, Number(node.config.outputs ?? "2") || 2) + 1,
                ),
              },
            }
          : node,
      ),
    );
  };

  const resetCanvasView = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const firstNode = nodes[0];
    canvas.scrollTo({
      left: Math.max(0, (firstNode?.x ?? 0) - 48),
      top: Math.max(0, (firstNode?.y ?? 0) - 48),
    });
  };

  const removeOutput = (nodeID: string, portID: string) => {
    const outputIndex = Number(portID.replace("branch-", ""));
    if (!outputIndex) return;
    const node = nodes.find((item) => item.id === nodeID);
    const outputCount = Math.max(2, Number(node?.config.outputs ?? "2") || 2);
    if (outputCount <= 2 || outputIndex > outputCount) return;

    recordHistory();
    setNodes((current) =>
      current.map((item) =>
        item.id === nodeID
          ? {
              ...item,
              config: { ...item.config, outputs: String(outputCount - 1) },
            }
          : item,
      ),
    );
    setEdges((current) =>
      current
        .filter((edge) => edge.source !== nodeID || edge.sourcePort !== portID)
        .map((edge) => {
          if (edge.source !== nodeID || !edge.sourcePort) return edge;
          const sourceIndex = Number(edge.sourcePort.replace("branch-", ""));
          return sourceIndex > outputIndex
            ? { ...edge, sourcePort: `branch-${sourceIndex - 1}` }
            : edge;
        }),
    );
  };

  const deleteNode = (nodeID: string) => {
    recordHistory();

    const remaining = nodes.filter((node) => node.id !== nodeID);
    setNodes(remaining);
    setSelectedIDs((current) => {
      const next = new Set(current);
      next.delete(nodeID);
      return next.size || !remaining.length ? next : new Set([remaining[0].id]);
    });
    setEdges((current) =>
      current.filter(
        (edge) => edge.source !== nodeID && edge.target !== nodeID,
      ),
    );
    if (selectedID === nodeID) setSelectedID(remaining[0]?.id ?? null);
    if (settingsNodeID === nodeID) setSettingsNodeID(null);
    setContextMenu(null);
    setSaved(false);
  };

  const openContextMenu = (event: ReactMouseEvent, node: WorkflowNode) => {
    event.preventDefault();
    const bounds = canvasRef.current?.getBoundingClientRect();
    if (!bounds) return;

    setSelectedID(node.id);
    setContextMenu({ nodeID: node.id, x: event.clientX, y: event.clientY });
  };

  const openWarningContextMenu = (event: ReactMouseEvent, warning: string) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({ warning, x: event.clientX, y: event.clientY });
  };

  const openCanvasContextMenu = (event: ReactMouseEvent) => {
    if (event.target instanceof Element && event.target.closest(".node")) {
      return;
    }
    event.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const bounds = canvas.getBoundingClientRect();
    setContextMenu({
      canvasPosition: {
        x: event.clientX - bounds.left + canvas.scrollLeft,
        y: event.clientY - bounds.top + canvas.scrollTop,
      },
      x: event.clientX,
      y: event.clientY,
    });
  };

  const copyNode = (nodeID: string) => {
    const node = nodes.find((item) => item.id === nodeID);
    if (!node) return;
    setCopiedNode(structuredClone(node));
    setContextMenu(null);
  };

  const pasteNode = (position: PortPosition) => {
    if (!copiedNode) return;
    const openPosition = findOpenNodePosition(
      nodes,
      position.x,
      position.y,
      copiedNode.kind,
    );
    const next = {
      ...structuredClone(copiedNode),
      id: `${copiedNode.kind}-${Date.now()}`,
      title: copiedNode.label,
      x: openPosition.x,
      y: openPosition.y,
    };
    recordHistory();
    setNodes((current) => [...current, next]);
    setSelectedIDs(new Set([next.id]));
    setSelectedID(next.id);
    setSaved(false);
    setContextMenu(null);
  };

  const openEdgeContextMenu = (event: ReactMouseEvent, edge: Edge) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedEdge(`${edge.source}-${edge.target}`);
    setContextMenu({ edge, x: event.clientX, y: event.clientY });
  };

  const openOutputContextMenu = (
    event: ReactMouseEvent,
    nodeID: string,
    portID: string,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({
      output: { nodeID, portID },
      x: event.clientX,
      y: event.clientY,
    });
  };

  const removeEdge = (edge: Edge) => {
    recordHistory();
    setEdges((current) =>
      current.filter(
        (item) => item.source !== edge.source || item.target !== edge.target,
      ),
    );
    setSelectedEdge(null);
    setContextMenu(null);
    setSaved(false);
  };

  const updateNodeConfig = (
    config: Record<string, string>,
    group?: GroupGraph,
    close = true,
  ): Record<string, string> | undefined => {
    if (!settingsNodeID) return undefined;

    const settingsNode = nodes.find((node) => node.id === settingsNodeID);
    const normalizedConfig =
      settingsNode?.kind === "display"
        ? {
            ...config,
            fps: String(
              Math.min(60, Math.max(1, Number(config.fps || "1") || 1)),
            ),
          }
        : group
          ? {
              ...config,
              inputs: String(group.inputs.length),
              outputs: String(group.outputs.length),
            }
          : config;

    recordHistory();
    setNodes((current) =>
      current.map((node) =>
        node.id === settingsNodeID
          ? {
              ...node,
              config: normalizedConfig,
              group: group ?? node.group,
              inputPorts: group
                ? group.inputs.map((port) => port.id)
                : node.inputPorts,
            }
          : node,
      ),
    );
    if (close) setSettingsNodeID(null);
    setSaved(false);
    return normalizedConfig;
  };

  const saveNodeConfig = (config: Record<string, string>, group?: GroupGraph) =>
    updateNodeConfig(config, group, true);

  const applyNodeConfig = (
    config: Record<string, string>,
    group?: GroupGraph,
  ) => {
    const normalizedConfig = updateNodeConfig(config, group, false);
    if (!settingsNodeID || !settingsNode || !normalizedConfig) {
      return Promise.resolve({
        logs: [],
        error: "Node configuration was not applied",
      });
    }
    return saveWorkflowToDatabase({
      nodes: nodes.map((node) =>
        node.id === settingsNodeID
          ? { ...node, config: normalizedConfig }
          : node,
      ),
      edges,
    }).then(() =>
      applyWorkflowNode(
        settingsNodeID,
        settingsNode.kind,
        normalizedConfig,
      ),
    );
  };

  const selectedNode = selectedID
    ? (nodes.find((node) => node.id === selectedID) ?? null)
    : null;
  const settingsNode = nodes.find((node) => node.id === settingsNodeID);
  const nodeByID = (id: string) => nodes.find((node) => node.id === id);
  const upstreamNodes = edges
    .filter((edge) => selectedNode && edge.target === selectedNode.id)
    .map((edge) => nodeByID(edge.source))
    .filter((node): node is WorkflowNode => Boolean(node));
  const settingsUpstreamNodes = settingsNode
    ? edges
        .filter((edge) => edge.target === settingsNode.id)
        .map((edge) => nodeByID(edge.source))
        .filter((node): node is WorkflowNode => Boolean(node))
    : [];
  const edgeCanvasHeight = Math.max(
    CANVAS_HEIGHT,
    ...nodes.map((node) => node.y + nodeHeight(node) + 48),
  );
  const validationIssues = useMemo(
    () => validateWorkflow(nodes, edges),
    [edges, nodes],
  );
  const validationByEdge = useMemo(
    () => new Map(validationIssues.map((issue) => [issue.edgeKey, issue])),
    [validationIssues],
  );
  const routedEdges = useMemo(
    () =>
      edges.flatMap((edge) => {
        const sourcePort = portPositions[edge.source]?.[edge.sourcePort ?? "output"];
        const targetPort = portPositions[edge.target]?.[edge.targetPort ?? "input"];
        if (!sourcePort || !targetPort) return [];
        return [{ edge, key: workflowEdgeKey(edge), points: routeOrthogonal(sourcePort, targetPort, nodeRects) }];
      }),
    [edges, nodeRects, portPositions],
  );
  const jumpsByEdge = useMemo(() => {
    const jumps = new Map<string, ReturnType<typeof edgeJumps>>();
    edgeJumps(routedEdges).forEach((jump) => {
      jumps.set(jump.key, [...(jumps.get(jump.key) ?? []), jump]);
    });
    return jumps;
  }, [routedEdges]);
  const validationByNode = useMemo(() => {
    const warnings = new Map<string, string[]>();
    validationIssues.forEach((issue) => {
      for (const nodeID of [issue.sourceNodeID, issue.targetNodeID]) {
        warnings.set(nodeID, [...(warnings.get(nodeID) ?? []), issue.message]);
      }
    });
    return warnings;
  }, [validationIssues]);
  const previewVersion = useMemo(
    () =>
      [
        ...edges.map((edge) =>
          [edge.source, edge.sourcePort ?? "", edge.target, edge.targetPort ?? ""].join(":"),
        ),
        ...nodes
          .filter((node) => node.kind === "depthMap")
          .map((node) => `${node.id}:${node.config.palette ?? "Jet"}`),
        ...nodes.map((node) =>
          `${node.id}:${JSON.stringify({ ...node.config, password: "" })}`,
        ),
      ].join("|"),
    [edges, nodes],
  );
  const saveWorkflow = async () => {
    try {
      await saveWorkflowToDatabase({ nodes, edges });
      setSaved(true);
      setLastSavedAt(new Date());
    } catch {
      setSaved(false);
    }
  };

  const toggleRuntime = async () => {
    try {
      setRuntimeError("");
      if (running) {
        await stopWorkflowRuntime();
        setRunning(false);
        return;
      }
      setRuntimeStarting(true);
      try {
        await saveWorkflowToDatabase({ nodes, edges });
        await startWorkflowRuntime();
        setRunning(true);
        setSaved(true);
        setLastSavedAt(new Date());
      } finally {
        setRuntimeStarting(false);
      }
    } catch (error) {
      setRuntimeError(
        error instanceof Error ? error.message : "Runtime unavailable",
      );
    }
  };

  return (
    <Layout
      activePath="/workflow"
      className={`workflow-page workflow-page--${mode}${inspectorOpen ? " inspector-open" : ""}`}
      style={{ "--inspector-width": `${inspectorWidth}px` } as CSSProperties}
    >
      <Box as="main" className="main">
        <header className="header">
          <div>
            <p className="eyebrow">Automation workspace</p>
            <h1 className="title">Workflow editor</h1>
          </div>
        </header>

        <Grid className="layout">
          <section className="canvas-section">
            <div className="section-header">
              <div className="section-tools">
                <Button
                  aria-pressed={moveMode}
                  onClick={() => setMoveMode((enabled) => !enabled)}
                  variant={moveMode ? "primary" : "secondary"}
                >
                  Move
                </Button>
                <Button onClick={resetCanvasView} variant="secondary">
                  Reset
                </Button>
                <Button
                  disabled={!nodes.length && !running}
                  aria-label={running ? "Stop workflow" : "Start workflow"}
                  className={`workflow-action-icon ${running ? "workflow-action-icon--stop" : "workflow-action-icon--play"}`}
                  title={running ? "Stop workflow" : "Start workflow"}
                  onClick={toggleRuntime}
                  variant={running ? "danger" : "secondary"}
                >
                  {running ? (
                    <svg aria-hidden="true" viewBox="0 0 24 24">
                      <rect height="10" rx="1" width="10" x="7" y="7" />
                    </svg>
                  ) : (
                    <svg aria-hidden="true" viewBox="0 0 24 24">
                      <path d="m8 5 11 7-11 7z" />
                    </svg>
                  )}
                </Button>
                {(mode !== "desktop" || !inspectorOpen) && (
                  <Button
                    aria-expanded={inspectorOpen}
                    onClick={() => setInspectorOpen((open) => !open)}
                    variant="secondary"
                  >
                    {inspectorOpen ? "Close inspector" : "Open inspector"}
                  </Button>
                )}
                <Button
                  disabled={!historyLength}
                  onClick={undo}
                  variant="ghost"
                >
                  Undo
                </Button>
                <Button
                  disabled={saved}
                  variant="primary"
                  onClick={saveWorkflow}
                >
                  {saved ? "Saved" : "Save"}
                </Button>
              </div>
            </div>

            <NodeCanvas
              canvasRef={canvasRef}
              canvasHeight={CANVAS_HEIGHT}
              canvasWidth={CANVAS_WIDTH}
              onPointerDown={startMarquee}
              onDragOver={allowNodeDrop}
              onDrop={dropNode}
              onClick={() => {
                setContextMenu(null);
                setSelectedEdge(null);
              }}
              onContextMenu={openCanvasContextMenu}
            >
              <svg
                aria-hidden="true"
                className="canvas-edges"
                height={edgeCanvasHeight}
                viewBox={`0 0 ${CANVAS_WIDTH} ${edgeCanvasHeight}`}
                width={CANVAS_WIDTH}
              >
                {routedEdges.map(({ edge, key, points }) => {
                  const source = nodeByID(edge.source);
                  const target = nodeByID(edge.target);
                  if (!source || !target) return null;
                  const invalid = validationByEdge.has(key);
                  const activityVersion = activityVersions[edge.source] ?? 0;
                  const highlighted = selectedEdge === `${edge.source}-${edge.target}`;
                  const stroke = invalid || highlighted ? "var(--color-warning)" : "var(--color-accent)";

                  return (
                    <g key={key}>
                      <path
                        d={pathFromPoints(points)}
                        fill="none"
                        onContextMenu={(event) => openEdgeContextMenu(event, edge)}
                        stroke={stroke}
                        strokeWidth={highlighted ? "4" : "2.5"}
                        className={activityVersion ? `workflow-edge--pulse-${activityVersion % 2}` : undefined}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      {(jumpsByEdge.get(key) ?? []).map((jump, index) => (
                        <g aria-hidden="true" key={`${key}-jump-${index}`}>
                          <path
                            className="workflow-edge-jump-mask"
                            d={jumpPath(jump)}
                            fill="none"
                            stroke="var(--color-surface-muted)"
                            strokeLinecap="round"
                            strokeWidth={highlighted ? "7" : "5.5"}
                          />
                          <path
                            className="workflow-edge-jump"
                            d={jumpPath(jump)}
                            fill="none"
                            stroke={stroke}
                            strokeLinecap="round"
                            strokeWidth={highlighted ? "4" : "2.5"}
                          />
                        </g>
                      ))}
                    </g>
                  );
                })}
                {connectionDrag?.side === "output" && connectionCursor && (
                  <>
                    {(() => {
                      const sourcePort =
                        portPositions[connectionDrag.nodeID]?.[
                          connectionDrag.portID
                        ];
                      if (!sourcePort) return null;
                      return (
                        <path
                          className="connection-preview"
                          d={pathFromPoints(routeOrthogonal(sourcePort, connectionCursor, nodeRects))}
                          fill="none"
                          stroke={
                            connectionInvalid
                              ? "var(--color-warning)"
                              : "var(--color-accent)"
                          }
                          strokeDasharray="8 6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="3"
                        />
                      );
                    })()}
                  </>
                )}
              </svg>
              {nodes.map((node) => (
                <Node
                  key={node.id}
                  node={node}
                  selected={selectedIDs.has(node.id)}
                  connectingFrom={connectingFrom}
                  running={running}
                  runtimeStarting={runtimeStarting}
                  onDragStart={startDrag}
                  onSelect={selectNode}
                  onOpenSettings={setSettingsNodeID}
                  onConnectionStart={startConnectionDrag}
                  connectionDrag={connectionDrag}
                  onConnect={connect}
                  onContextMenu={openContextMenu}
                  onWarningContextMenu={openWarningContextMenu}
                  onAddOutput={addOutput}
                  onOutputContextMenu={openOutputContextMenu}
                  validationWarnings={validationByNode.get(node.id) ?? []}
                  hasInput={edges.some((edge) => edge.target === node.id)}
                  hasAudioInput={edges.some(
                    (edge) =>
                      edge.target === node.id &&
                      node.kind === "display" &&
                      edge.targetPort === "audio",
                  )}
                  activityVersion={activityVersions[node.id] ?? 0}
                  previewVersion={previewVersion}
                  onFrame={reportFrame}
                />
              ))}
              {marquee && (
                <div
                  className="selection-marquee"
                  style={{
                    left: Math.min(marquee.startX, marquee.currentX),
                    top: Math.min(marquee.startY, marquee.currentY),
                    width: Math.abs(marquee.currentX - marquee.startX),
                    height: Math.abs(marquee.currentY - marquee.startY),
                  }}
                />
              )}
            </NodeCanvas>

            {contextMenu && (
              <ContextMenu
                items={
                  contextMenu.output
                    ? [
                        {
                          label: "Remove output",
                          variant: "danger" as const,
                          disabled:
                            (Number(
                              nodes.find(
                                (node) =>
                                  node.id === contextMenu.output?.nodeID,
                              )?.config.outputs ?? "2",
                            ) || 2) <= 2,
                          onClick: () => {
                            removeOutput(
                              contextMenu.output!.nodeID,
                              contextMenu.output!.portID,
                            );
                            setContextMenu(null);
                          },
                        },
                      ]
                    : contextMenu.warning
                      ? [
                          {
                            label: "Copy warning",
                            onClick: () => {
                              void navigator.clipboard.writeText(
                                contextMenu.warning!,
                              );
                              setContextMenu(null);
                            },
                          },
                        ]
                      : contextMenu.edge
                      ? [
                          {
                            label: "Remove connection",
                            variant: "danger" as const,
                            onClick: () => removeEdge(contextMenu.edge!),
                          },
                        ]
                      : contextMenu.canvasPosition
                        ? [
                            {
                              label: "Open node palette",
                              onClick: () => {
                                setAddNodePosition(contextMenu.canvasPosition!);
                                setInspectorOpen(true);
                                setContextMenu(null);
                              },
                            },
                            {
                              label: "Paste",
                              disabled: !copiedNode,
                              onClick: () =>
                                pasteNode(contextMenu.canvasPosition!),
                            },
                          ]
                        : [
                            {
                              label: "Settings",
                              onClick: () => {
                                setSelectedID(contextMenu.nodeID!);
                                setSettingsNodeID(contextMenu.nodeID!);
                                setContextMenu(null);
                              },
                            },
                            {
                              label: "Copy",
                              onClick: () => copyNode(contextMenu.nodeID!),
                            },
                            {
                              label: "Delete",
                              variant: "danger" as const,
                              onClick: () => deleteNode(contextMenu.nodeID!),
                            },
                          ]
                }
                x={contextMenu.x}
                y={contextMenu.y}
              />
            )}
          </section>

          <WorkflowSidebar
            activityVersion={selectedNode ? (activityVersions[selectedNode.id] ?? 0) : 0}
            definitions={nodeDefinitions}
            key={selectedNode?.id ?? "none"}
            onAddNode={addNode}
            onClose={() => setInspectorOpen(false)}
            onOpenConfig={setSettingsNodeID}
            onResizeStart={(event) => {
              event.preventDefault();
              inspectorResizeRef.current = true;
              document.body.style.cursor = "col-resize";
              document.body.style.userSelect = "none";
            }}
            selectedNode={selectedNode}
            upstreamNodes={settingsUpstreamNodes}
            running={running}
          />
        </Grid>
        <footer className="footer">
          <span className="footer-status footer-runtime-status">
            <span
              className={`footer-status-dot ${runtimeError ? "footer-status-dot--error" : running ? "footer-status-dot--running" : ""}`}
            />
            {runtimeError ? "Error" : running ? "Running" : "Stopped"}
          </span>
          <span className="footer-save-status">
            {saved && lastSavedAt
              ? `Saved ${lastSavedAt.toLocaleTimeString()}`
              : "Unsaved changes"}
          </span>
          <span className="footer-count">
            {nodes.length} nodes · {edges.length} connections
          </span>
        </footer>
        {settingsNode && (
          <NodeConfig
            node={settingsNode}
            hasInput={edges.some((edge) => edge.target === settingsNode.id)}
            hasAudioInput={edges.some(
              (edge) =>
                edge.target === settingsNode.id &&
                settingsNode.kind === "display" &&
                edge.targetPort === "audio",
            )}
            runtimeRunning={running}
            upstreamNodes={upstreamNodes}
            onClose={() => setSettingsNodeID(null)}
            onApply={applyNodeConfig}
            onSave={saveNodeConfig}
          />
        )}
      </Box>
    </Layout>
  );
}
